import { useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Basketball, MegaphoneSimple, ArrowRight } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shown once, right after a first-time Google sign-in.
 * Requires state.temp_token from /auth/callback — direct visits redirect to /login.
 */
const RoleCard = ({ selected, onSelect, icon, title, body, testId }) => (
  <button
    type="button"
    onClick={onSelect}
    className={`text-left p-5 rounded-2xl border-2 transition-all ${
      selected ? 'border-[#F97316] bg-orange-50 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'
    }`}
    data-testid={testId}
  >
    <div className="flex items-start gap-3">
      <div className={`p-2.5 rounded-xl ${selected ? 'bg-[#F97316] text-white' : 'bg-gray-100 text-gray-700'}`}>{icon}</div>
      <div className="flex-1">
        <div className="font-bold text-gray-900">{title}</div>
        <div className="text-xs text-gray-600 mt-0.5">{body}</div>
      </div>
    </div>
  </button>
);

const roleDashboard = (role) => (role === 'organizer' ? '/organizer' : '/player-dashboard');

const PickRole = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const state = location.state || {};
  const [role, setRole] = useState('player');
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!state.temp_token) {
    return <Navigate to="/login" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/google/complete-role`,
        { temp_token: state.temp_token, role, city, phone },
        { withCredentials: true }
      );
      await checkAuth();
      navigate(roleDashboard(data.user?.role), { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not finish sign-in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-24 court-pattern">
      <div className="max-w-lg w-full">
        <div className="text-center mb-8 animate-fade-up">
          {state.picture && <img src={state.picture} alt={state.name || 'You'} className="h-16 w-16 rounded-full mx-auto mb-4 border-4 border-white shadow-md" />}
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 mb-2" data-testid="pick-role-title">One last step</h1>
          <p className="text-gray-600">Welcome{state.name ? `, ${state.name}` : ''} — how will you use Beyond Ball India?</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm animate-fade-up delay-100">
          {error && (
            <div className="bg-red-50 border border-red-100 text-red-700 rounded-xl px-4 py-3 mb-4 text-sm" data-testid="pick-role-error">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <RoleCard
                selected={role === 'player'}
                onSelect={() => setRole('player')}
                icon={<Basketball size={20} weight="bold" />}
                title="I'm a Player"
                body="Discover tournaments and register your team."
                testId="pick-role-player-btn"
              />
              <RoleCard
                selected={role === 'organizer'}
                onSelect={() => setRole('organizer')}
                icon={<MegaphoneSimple size={20} weight="bold" />}
                title="I'm an Organizer"
                body="List tournaments and manage registrations."
                testId="pick-role-organizer-btn"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">City <span className="text-gray-400 font-normal">(optional)</span></label>
                <input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#F97316] focus:ring-2 focus:ring-orange-100 rounded-xl text-gray-900 px-4 py-3 text-sm outline-none transition-all"
                  data-testid="pick-role-city-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone <span className="text-gray-400 font-normal">(optional)</span></label>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-gray-50 border border-gray-200 focus:bg-white focus:border-[#F97316] focus:ring-2 focus:ring-orange-100 rounded-xl text-gray-900 px-4 py-3 text-sm outline-none transition-all"
                  data-testid="pick-role-phone-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full px-5 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="pick-role-submit-btn"
            >
              {submitting ? 'Setting up…' : 'Continue'}
              {!submitting && <ArrowRight size={16} weight="bold" />}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PickRole;
